import React, { useCallback, useEffect, useState } from 'react';
import { tenantAPI } from '../api/client';
import { useAuth } from '../context/AuthContext';
import { useTenant } from '../context/TenantContext';
import { apiError, appUrl, goTo } from '../lib/host';
import './AdminDashboard.css';

const ROLES = ['owner', 'admin', 'member', 'viewer'];
const RANK = { viewer: 0, member: 1, admin: 2, owner: 3 };

// Mirrors backend permissions.can_manage; the server enforces it regardless.
const canManage = (actor, target) => actor === 'owner' || (RANK[actor] >= RANK.admin && target !== 'owner');

export default function Members() {
  const { user, refresh } = useAuth();
  const { tenant, role: myRole, can } = useTenant();
  const [members, setMembers] = useState([]);
  const [invites, setInvites] = useState([]);
  const [invite, setInvite] = useState({ email: '', role: 'member' });
  const [inviteLink, setInviteLink] = useState('');
  const [error, setError] = useState('');
  const [busy, setBusy] = useState(false);

  const load = useCallback(() => {
    tenantAPI.members().then((res) => setMembers(res.data)).catch((err) => setError(apiError(err)));
    if (can('members.invite')) {
      tenantAPI.invitations().then((res) => setInvites(res.data)).catch(() => {});
    }
  }, [can]);

  useEffect(load, [load]);

  const run = async (fn) => {
    setBusy(true);
    setError('');
    try {
      const res = await fn();
      load();
      return res;
    } catch (err) {
      setError(apiError(err));
      return null;
    } finally {
      setBusy(false);
    }
  };

  const sendInvite = async (e) => {
    e.preventDefault();
    const res = await run(() => tenantAPI.invite(invite.email, invite.role));
    if (res) {
      setInviteLink(res.data.invite_url);
      setInvite({ email: '', role: 'member' });
    }
  };

  const leave = async () => {
    if (!window.confirm(`Leave ${tenant.name}? You'll lose access immediately.`)) return;
    const res = await run(() => tenantAPI.leave());
    if (res) {
      await refresh();
      goTo(appUrl('/select-org'));
    }
  };

  const grantable = ROLES.filter((r) => RANK[r] <= RANK[myRole]);

  return (
    <>
      {error && <div className="admin-error">{error}</div>}

      {can('members.invite') && (
        <section className="tcx-panel">
          <h3>Invite a teammate</h3>
          <form className="tcx-row" onSubmit={sendInvite}>
            <div className="form-group">
              <label>Email</label>
              <input className="tcx-input" type="email" required value={invite.email}
                onChange={(e) => setInvite({ ...invite, email: e.target.value })} />
            </div>
            <div className="form-group" style={{ flex: '0 0 140px' }}>
              <label>Role</label>
              <select className="tcx-select" value={invite.role} onChange={(e) => setInvite({ ...invite, role: e.target.value })}>
                {grantable.map((r) => <option key={r} value={r}>{r}</option>)}
              </select>
            </div>
            <button className="tcx-btn" disabled={busy}>Send invite</button>
          </form>
          {inviteLink && (
            <div className="tcx-copy">
              <input className="tcx-input" readOnly value={inviteLink} onFocus={(e) => e.target.select()} />
              <button className="tcx-btn secondary" type="button" onClick={() => navigator.clipboard?.writeText(inviteLink)}>Copy link</button>
            </div>
          )}

          {invites.length > 0 && (
            <div className="tcx-table-wrap" style={{ marginTop: 16 }}>
              <table className="tcx-table">
                <thead><tr><th>Pending invite</th><th>Role</th><th>Expires</th><th /></tr></thead>
                <tbody>
                  {invites.map((i) => (
                    <tr key={i.id}>
                      <td>{i.email}</td>
                      <td><span className={`tcx-role tcx-role-${i.role}`}>{i.role}</span></td>
                      <td>{new Date(i.expires_at + 'Z').toLocaleDateString()}</td>
                      <td><button className="tcx-btn danger" disabled={busy} onClick={() => run(() => tenantAPI.revokeInvitation(i.id))}>Revoke</button></td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </section>
      )}

      <section className="tcx-panel">
        <h3>Members of {tenant.name}</h3>
        <div className="tcx-table-wrap">
          <table className="tcx-table">
            <thead><tr><th>Name</th><th>Email</th><th>Role</th><th>Status</th><th>Joined</th><th /></tr></thead>
            <tbody>
              {members.map((m) => {
                const self = m.user_id === user.id;
                const manageable = !self && can('members.update_role') && canManage(myRole, m.role);
                return (
                  <tr key={m.user_id}>
                    <td>{m.name}{self && ' (you)'}</td>
                    <td>{m.email}</td>
                    <td>
                      {manageable ? (
                        <select className="tcx-select" value={m.role} disabled={busy}
                          onChange={(e) => run(() => tenantAPI.updateMember(m.user_id, { role: e.target.value }))}>
                          {grantable.map((r) => <option key={r} value={r}>{r}</option>)}
                        </select>
                      ) : <span className={`tcx-role tcx-role-${m.role}`}>{m.role}</span>}
                    </td>
                    <td className={m.status === 'suspended' ? 'tcx-status-suspended' : ''}>{m.status}</td>
                    <td>{m.joined_at ? new Date(m.joined_at + 'Z').toLocaleDateString() : '—'}</td>
                    <td>
                      <div className="tcx-actions">
                        {manageable && (
                          <button className="tcx-btn secondary" disabled={busy}
                            onClick={() => run(() => tenantAPI.updateMember(m.user_id, { status: m.status === 'active' ? 'suspended' : 'active' }))}>
                            {m.status === 'active' ? 'Suspend' : 'Reactivate'}
                          </button>
                        )}
                        {manageable && can('members.remove') && (
                          <button className="tcx-btn danger" disabled={busy}
                            onClick={() => window.confirm(`Remove ${m.name} from ${tenant.name}?`) && run(() => tenantAPI.removeMember(m.user_id))}>
                            Remove
                          </button>
                        )}
                        {self && <button className="tcx-btn danger" disabled={busy} onClick={leave}>Leave</button>}
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </section>
    </>
  );
}
